// View 3 — Tenant detail drawer.
//
// Per design/UI : UX v1.0/for-engineering-tenants/specs/tenants-strings.md.
// Mirrors UserDetailDrawer 1:1 in structure (header → meta grid → edit
// section → empty tenant-users placeholder → danger footer + confirm
// panels).
//
// V1 RBAC:
//   - super_admin: all edit + lifecycle actions visible
//   - customer_success: read-only (no footer buttons)
//   - other roles: don't see the page at all (filtered by nav RBAC)
//
// Lifecycle:
//   - active   → footer shows Suspend (ghost) + Remove (danger)
//   - suspended → footer shows Reactivate (ghost) + Remove (danger)
//   - removed  → footer shows Restore (ghost; super_admin-only, disabled
//                with tooltip for everyone else)

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Drawer,
  Spinner,
} from '@gc-coach/design-system';
import type { TenantDetail, TenantStanding } from '@gc-coach/shared';
import {
  deleteTenant,
  getTenant,
  patchTenant,
  reactivateTenant,
  restoreTenant,
  suspendTenant,
} from '../api/endpoints.js';
import { useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import { formatRelativeTime } from '../users/time.js';
import { flagFromIso } from './flag.js';
import styles from './TenantDetailDrawer.module.css';

type ConfirmKind = null | 'suspend' | 'remove' | 'restore';

export interface TenantDetailDrawerProps {
  tenantId: string;
  onClose: () => void;
  onChanged: () => void;
  onRemoved: () => void;
}

export function TenantDetailDrawer({
  tenantId,
  onClose,
  onChanged,
  onRemoved,
}: TenantDetailDrawerProps) {
  const { t, tt } = useI18n();
  const navigate = useNavigate();
  const { session } = useSession();
  const isSuperAdmin = session?.roles.includes('super_admin') ?? false;

  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionPending, setActionPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [restoreConfirmText, setRestoreConfirmText] = useState('');

  // Inline edit local state (Super Admin only)
  const [editName, setEditName] = useState('');
  const [editBillingEmail, setEditBillingEmail] = useState('');
  const [editPlanTier, setEditPlanTier] = useState('');
  const [editSeatCount, setEditSeatCount] = useState('');

  // Initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const result = await getTenant(tenantId);
      if (cancelled) return;
      if (result.ok) {
        setTenant(result.data.tenant);
        // Seed edit fields from server values
        setEditName(result.data.tenant.name);
        setEditBillingEmail(result.data.tenant.billingEmail ?? '');
        setEditPlanTier(result.data.tenant.planTier ?? '');
        setEditSeatCount(
          result.data.tenant.seatCount !== null ? String(result.data.tenant.seatCount) : '',
        );
      } else {
        setError(t('tenants.detailGenericError'));
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, t]);

  // ─── Action wrappers ───────────────────────────────────────────────

  async function runWithUpdate(
    op: () => Promise<
      { ok: true; data: { tenant: TenantDetail } } | { ok: false; code: string }
    >,
  ) {
    setActionPending(true);
    setError(null);
    const result = await op();
    setActionPending(false);
    if (result.ok) {
      setTenant(result.data.tenant);
      onChanged();
    } else if (result.code === 'FORBIDDEN') {
      setError(t('tenants.detailRestoreForbidden'));
    } else {
      setError(t('tenants.detailGenericError'));
    }
  }

  async function onChangeStanding(next: TenantStanding) {
    if (!tenant || tenant.standing === next) return;
    await runWithUpdate(() => patchTenant(tenant.id, { standing: next }));
  }

  async function onSaveMetadata() {
    if (!tenant) return;
    const seatCountNum = editSeatCount.trim() ? Number(editSeatCount.trim()) : null;
    if (seatCountNum !== null && (!Number.isFinite(seatCountNum) || seatCountNum < 0)) {
      setError(t('tenants.fieldErrorInvalidNumber'));
      return;
    }
    await runWithUpdate(() =>
      patchTenant(tenant.id, {
        name: editName.trim(),
        billingEmail: editBillingEmail.trim() ? editBillingEmail.trim().toLowerCase() : null,
        planTier: editPlanTier.trim() || null,
        seatCount: seatCountNum,
      }),
    );
  }

  async function onSuspendConfirmed() {
    if (!tenant) return;
    setConfirm(null);
    await runWithUpdate(() => suspendTenant(tenant.id));
  }

  async function onReactivate() {
    if (!tenant) return;
    await runWithUpdate(() => reactivateTenant(tenant.id));
  }

  async function onRemoveConfirmed() {
    if (!tenant) return;
    const reason = removeReason.trim() || undefined;
    setConfirm(null);
    setRemoveReason('');
    setActionPending(true);
    setError(null);
    const result = await deleteTenant(tenant.id, reason);
    setActionPending(false);
    if (result.ok) {
      onRemoved();
    } else {
      setError(t('tenants.detailGenericError'));
    }
  }

  async function onRestoreConfirmed() {
    if (!tenant) return;
    if (restoreConfirmText !== 'RESTORE') return;
    setConfirm(null);
    setRestoreConfirmText('');
    await runWithUpdate(() => restoreTenant(tenant.id));
  }

  // ─── Derived ──────────────────────────────────────────────────────

  const hasEdits = useMemo(() => {
    if (!tenant) return false;
    return (
      editName.trim() !== tenant.name ||
      (editBillingEmail.trim() || null) !== (tenant.billingEmail ?? null) ||
      (editPlanTier.trim() || null) !== (tenant.planTier ?? null) ||
      (editSeatCount.trim() ? Number(editSeatCount.trim()) : null) !==
        (tenant.seatCount ?? null)
    );
  }, [tenant, editName, editBillingEmail, editPlanTier, editSeatCount]);

  // The slug preview uses the staging hostname for V1. Phase 1 will read
  // from env.tenantDomainSuffix.
  const slugDomain = 'staging.gc-coach.ai';

  return (
    <Drawer
      open
      onClose={actionPending ? () => undefined : onClose}
      title={tenant ? tenant.name : ''}
      footer={
        tenant && isSuperAdmin ? (
          <div className={styles.dangerFooter}>
            {tenant.status === 'removed' ? (
              <Button
                kind="ghost"
                onClick={() => setConfirm('restore')}
                disabled={actionPending}
              >
                {t('tenants.detailRestore')}
              </Button>
            ) : (
              <>
                {tenant.status === 'suspended' ? (
                  <Button kind="ghost" onClick={onReactivate} disabled={actionPending}>
                    {t('tenants.detailReactivate')}
                  </Button>
                ) : (
                  <Button
                    kind="ghost"
                    onClick={() => setConfirm('suspend')}
                    disabled={actionPending}
                  >
                    {t('tenants.detailSuspend')}
                  </Button>
                )}
                <Button
                  kind="danger"
                  onClick={() => setConfirm('remove')}
                  disabled={actionPending}
                >
                  {t('tenants.detailRemove')}
                </Button>
              </>
            )}
          </div>
        ) : tenant && !isSuperAdmin && tenant.status === 'removed' ? (
          <div className={styles.dangerFooter}>
            <Button
              kind="ghost"
              disabled
              title={t('tenants.detailRestoreSuperAdminOnly')}
            >
              {t('tenants.detailRestore')}
            </Button>
          </div>
        ) : null
      }
    >
      {isLoading && (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      )}

      {!isLoading && !tenant && (
        <Alert severity="danger">{t('tenants.detailGenericError')}</Alert>
      )}

      {tenant && (
        <div className={styles.body}>
          {error && (
            <div className={styles.alert}>
              <Alert severity="danger">{error}</Alert>
            </div>
          )}

          {/* Header */}
          <header className={styles.header}>
            <div className={styles.headerText}>
              <span className={styles.headerName}>{tenant.name}</span>
              <span className={styles.headerEmail}>
                {tenant.slug}.{slugDomain}
              </span>
            </div>
            <span className={statusClass(tenant.status, styles)}>
              {statusText(tenant.status, t)}
            </span>
          </header>

          {/* Meta grid */}
          <dl className={styles.metaGrid}>
            <MetaCell label={t('tenants.detailMetaJurisdiction')}>
              <span>
                <span aria-hidden="true">{flagFromIso(tenant.jurisdiction)}</span>{' '}
                {tenant.jurisdiction}
              </span>
            </MetaCell>
            <MetaCell label={t('tenants.detailMetaRegion')}>
              <span className={styles.mono}>{tenant.region.toUpperCase()}</span>
            </MetaCell>
            <MetaCell label={t('tenants.detailMetaStanding')}>
              <span>{standingLabel(tenant.standing, t)}</span>
            </MetaCell>
            <MetaCell label={t('tenants.detailMetaUsers')}>
              <span>{tenant.userCount}</span>
            </MetaCell>
            <MetaCell label={t('tenants.detailMetaCreatedBy')}>
              <span>{tenant.createdBy}</span>
            </MetaCell>
            <MetaCell label={t('tenants.detailMetaCreatedAt')}>
              <span>{formatRelativeTime(tenant.createdAt)}</span>
            </MetaCell>
          </dl>

          {/* Slug — read-only with permanence note */}
          <section className={styles.section}>
            <span className={styles.sectionLabel}>{t('tenants.detailSlug')}</span>
            <div className={styles.mono}>{tenant.slug}</div>
            <p className={styles.sessionsBody}>{t('tenants.detailSlugPermanent')}</p>
          </section>

          {/* Edit fields (super_admin only) */}
          {isSuperAdmin && (
            <section className={styles.section}>
              <span className={styles.sectionLabel}>{t('tenants.detailEditMetadata')}</span>
              <div className={styles.editStack}>
                <EditField
                  label={t('tenants.fieldName')}
                  value={editName}
                  onChange={setEditName}
                  disabled={actionPending || tenant.status === 'removed'}
                />
                <EditField
                  label={t('tenants.fieldBillingEmail')}
                  value={editBillingEmail}
                  onChange={setEditBillingEmail}
                  disabled={actionPending || tenant.status === 'removed'}
                />
                <EditField
                  label={t('tenants.fieldPlanTier')}
                  value={editPlanTier}
                  onChange={setEditPlanTier}
                  disabled={actionPending || tenant.status === 'removed'}
                />
                <EditField
                  label={t('tenants.fieldSeatCount')}
                  value={editSeatCount}
                  onChange={setEditSeatCount}
                  type="number"
                  disabled={actionPending || tenant.status === 'removed'}
                />
                <div>
                  <Button
                    kind="ghost"
                    onClick={() => void onSaveMetadata()}
                    disabled={!hasEdits || actionPending || tenant.status === 'removed'}
                  >
                    {t('tenants.detailSaveMetadata')}
                  </Button>
                </div>
              </div>
            </section>
          )}

          {/* Change standing (super_admin only; not for removed tenants) */}
          {isSuperAdmin && tenant.status !== 'removed' && (
            <section className={styles.section}>
              <label className={styles.sectionLabel} htmlFor="change-standing">
                {t('tenants.detailChangeStanding')}
              </label>
              <select
                id="change-standing"
                className={styles.roleSelect}
                value={tenant.standing}
                onChange={(e) => void onChangeStanding(e.target.value as TenantStanding)}
                disabled={actionPending}
              >
                <option value="trial">{t('tenants.standingTrial')}</option>
                <option value="active">{t('tenants.standingActive')}</option>
                <option value="past_due">{t('tenants.standingPastDue')}</option>
                <option value="churned">{t('tenants.standingChurned')}</option>
              </select>
            </section>
          )}

          {/* Tenant users — quick link to the dedicated management page. */}
          <section className={styles.section}>
            <span className={styles.sectionLabel}>{t('tenants.detailTenantUsers')}</span>
            <p className={styles.sessionsBody}>{t('tenants.detailTenantUsersHint')}</p>
            <Button
              kind="ghost"
              onClick={() => navigate(`/admin/customers/tenants/${tenant.id}/users`)}
              disabled={tenant.status === 'removed'}
            >
              {t('tenants.detailTenantUsersManage')}
            </Button>
          </section>

          {/* Removal metadata (visible on removed tenants) */}
          {tenant.status === 'removed' && (
            <section className={styles.section}>
              <span className={styles.sectionLabel}>{t('tenants.detailRemovalMeta')}</span>
              <p className={styles.sessionsBody}>
                {tt('tenants.detailRemovedAtTemplate', {
                  when: tenant.removedAt
                    ? formatRelativeTime(tenant.removedAt)
                    : '—',
                  by: tenant.removedBy ?? '—',
                })}
                {tenant.removedReason ? ` — ${tenant.removedReason}` : ''}
              </p>
            </section>
          )}

          {/* Confirm panels (inline in drawer body) */}
          {confirm === 'suspend' && (
            <ConfirmPanel
              title={t('tenants.detailConfirmSuspend')}
              body={t('tenants.detailConfirmSuspendBody')}
              confirmLabel={t('tenants.detailConfirmYes')}
              cancelLabel={t('tenants.detailConfirmCancel')}
              tone="warn"
              onConfirm={onSuspendConfirmed}
              onCancel={() => setConfirm(null)}
            />
          )}
          {confirm === 'remove' && (
            <RemoveConfirmPanel
              title={t('tenants.detailConfirmRemove')}
              body={t('tenants.detailConfirmRemoveBody')}
              reasonLabel={t('tenants.detailConfirmRemoveReasonLabel')}
              reasonPlaceholder={t('tenants.detailConfirmRemoveReasonPlaceholder')}
              confirmLabel={t('tenants.detailConfirmYes')}
              cancelLabel={t('tenants.detailConfirmCancel')}
              reason={removeReason}
              setReason={setRemoveReason}
              onConfirm={onRemoveConfirmed}
              onCancel={() => {
                setConfirm(null);
                setRemoveReason('');
              }}
            />
          )}
          {confirm === 'restore' && (
            <RestoreConfirmPanel
              title={t('tenants.detailConfirmRestore')}
              body={t('tenants.detailConfirmRestoreBody')}
              typeToConfirmLabel={t('tenants.detailConfirmRestoreTypeLabel')}
              expectedToken="RESTORE"
              confirmLabel={t('tenants.detailConfirmRestore')}
              cancelLabel={t('tenants.detailConfirmCancel')}
              value={restoreConfirmText}
              setValue={setRestoreConfirmText}
              onConfirm={onRestoreConfirmed}
              onCancel={() => {
                setConfirm(null);
                setRestoreConfirmText('');
              }}
            />
          )}
        </div>
      )}
    </Drawer>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────────────────

function MetaCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.metaCell}>
      <dt className={styles.metaLabel}>{label}</dt>
      <dd className={styles.metaValue}>{children}</dd>
    </div>
  );
}

function EditField({
  label,
  value,
  onChange,
  disabled,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
  type?: string;
}) {
  return (
    <label className={styles.editFieldRow}>
      <span className={styles.editFieldLabel}>{label}</span>
      <input
        type={type}
        className={styles.editFieldInput}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </label>
  );
}

function ConfirmPanel({
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  tone,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  tone: 'warn' | 'danger';
}) {
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function RemoveConfirmPanel({
  title,
  body,
  reasonLabel,
  reasonPlaceholder,
  confirmLabel,
  cancelLabel,
  reason,
  setReason,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  confirmLabel: string;
  cancelLabel: string;
  reason: string;
  setReason: (s: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <label className={styles.confirmLabel} htmlFor="tenant-remove-reason">
        {reasonLabel}
      </label>
      <textarea
        id="tenant-remove-reason"
        className={styles.confirmTextarea}
        rows={3}
        maxLength={500}
        value={reason}
        placeholder={reasonPlaceholder}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind="danger" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function RestoreConfirmPanel({
  title,
  body,
  typeToConfirmLabel,
  expectedToken,
  confirmLabel,
  cancelLabel,
  value,
  setValue,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  typeToConfirmLabel: string;
  expectedToken: string;
  confirmLabel: string;
  cancelLabel: string;
  value: string;
  setValue: (s: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const enabled = value === expectedToken;
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <label className={styles.confirmLabel} htmlFor="tenant-restore-confirm">
        {typeToConfirmLabel}
      </label>
      <input
        id="tenant-restore-confirm"
        type="text"
        className={styles.confirmInput}
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind="primary" onClick={onConfirm} disabled={!enabled}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

// ─── Status / standing helpers ────────────────────────────────────────────

function statusClass(s: TenantDetail['status'], cls: Record<string, string>): string {
  if (s === 'active') return cls['statusActive'] ?? '';
  if (s === 'suspended') return cls['statusSuspended'] ?? '';
  return cls['statusRemoved'] ?? '';
}

function statusText(s: TenantDetail['status'], t: (k: string) => string): string {
  if (s === 'active') return t('tenants.statusActive');
  if (s === 'suspended') return t('tenants.statusSuspended');
  return t('tenants.statusRemoved');
}

function standingLabel(s: TenantStanding, t: (k: string) => string): string {
  if (s === 'trial') return t('tenants.standingTrial');
  if (s === 'active') return t('tenants.standingActive');
  if (s === 'past_due') return t('tenants.standingPastDue');
  return t('tenants.standingChurned');
}
